<template>
  <div class="index" />
</template>

<script>
/**
 * Vue: Index (core)
 * -----------------------------------------------------------------------------
 * Used to set index states and global event listeners.
 * - Also updates component loading states when overlays become active.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import breakpoints from '~/config/breakpoints'
import { loginEvent } from '~/helpers/datalayer-actions'
import { debounce, isCdnUrl, throttle } from '~/helpers/general'

import config from '~/../../canvas.config'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Index',

  data() {
    return {
      cnvsGlobal: true,
      screen: {
        breakpoint: 'xs',
        mobile: true,
        height: 0,
        width: 0,
      },
      scroll: {
        direction: 'none',
        header: 'open',
        top: true,
        x: 0,
        y: 0,
      },
      rebuyAdd: false,
      gwpWatcher: null,
    }
  },

  computed: {
    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      activeOverlays: (state) => state.overlays.active,
      cart: (state) => state.cart,
      pausedScroll: (state) => state.index.scroll.paused,
      settings: (state) => state.index.settings,
      geolocation: (state) => state.geolocation,
    }),
  },

  watch: {
    /**
     * Watch active overlay to update component loading state.
     */
    getActiveOverlay(overlay) {
      document.body.classList[overlay ? 'add' : 'remove']('has-overlay')

      if (!overlay?.component) {
        return
      }

      const components = Array.isArray(overlay.component) ? overlay.component : [overlay.component]

      components.forEach((handle) => {
        this.loadComponent(handle)
      })
    },
  },

  created() {
    this.gwpWatcher = this.$watch('cart.response', this.handleGwp)
    this.clearAccountData()
    this.handleGeolocation()
  },

  mounted() {
    this.rehydrateSite()
    this.setMobileBreakpoints(config.mobileBreakpoints)
    this.getPreviousUrl()

    this.handleResize()
    this.handleScroll(true)

    this.setEventListeners()
    this.setEventBusListeners()

    this.handleNewsletterResponse()
    this.handleAccountSuccess()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      loadComponent: 'components/load',
      loadStylesheet: 'components/loadStylesheet',
      openOverlay: 'overlays/open',
      rehydrateSite: 'rehydrateSite',
      removeItem: 'cart/updateItem',
      setCartResponse: 'cart/setResponse',
      setMobileBreakpoints: 'setMobileBreakpoints',
      setPreviousUrl: 'setPreviousUrl',
      setScreen: 'setScreen',
      setScroll: 'setScroll',
      setStylesheet: 'setStylesheet',
      setGeolocation: 'geolocation/setGeolocation',
    }),

    /**
     * Get previous URL from sessionStorage.
     * - State doesn't update in time if set in the `beforeunload` event.
     */
    getPreviousUrl() {
      const previousUrl = sessionStorage.getItem('previousUrl')

      if (previousUrl) {
        this.setPreviousUrl(previousUrl)
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.setStylesheetListeners()

      /**
       * Add default listeners.
       */
      window.visualViewport.addEventListener(
        'resize',
        debounce(() => this.handleResize(), this.settings.throttle),
        { passive: true },
      )

      window.addEventListener(
        'scroll',
        throttle(() => this.handleScroll(), this.settings.throttle),
        { passive: true },
      )

      /**
       * Set previous URL just before changing page.
       */
      window.addEventListener('beforeunload', () => {
        sessionStorage.setItem('previousUrl', location.pathname)
      })

      /**
       * Refresh cart with Rebuy updates
       */
      document.addEventListener('rebuy:cart.add', (payload) => {
        const addedItem = payload.detail?.item?.items[0]

        // eslint-disable-next-line
        if (addedItem?.properties?._momentMaker) {
          return
        }

        this.rebuyAdd = true
      })

      document.addEventListener('rebuy:cart.change', () => {
        cnvs.EventBus.emit('cart:update', this.rebuyAdd)
        this.rebuyAdd = false
      })
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      /**
       * Overlay listeners act as fallbacks for non-Vue components.
       * - Also used by cnvs.Helpers.openOverlay() CLI.
       */
      cnvs.EventBus.listen('overlay:open', (overlay) => {
        this.openOverlay(overlay)

        if (overlay.component || !overlay.stylesheet) {
          return
        }

        this.loadStylesheet(overlay.stylesheet)
      })

      cnvs.EventBus.listen('overlay:close', (overlay) => {
        this.closeOverlay(overlay)
      })

      /**
       * Cart update fallback listener.
       */
      cnvs.EventBus.listen('cart:update', async (open = true) => {
        await this.setCartResponse()
      })
    },

    /**
     * Listen for stylesheets loading to trigger events after they've loaded.
     */
    setStylesheetListeners() {
      const stylesheets = [...document.querySelectorAll('[rel="stylesheet"]')]

      stylesheets.forEach((element) => {
        const media = element.getAttribute('media')
        let stylesheet = element.getAttribute('href')

        if (isCdnUrl(stylesheet)) {
          stylesheet = stylesheet.split('/').reverse()[0].split('?')[0]
        }

        if (!stylesheet) {
          return
        }

        /**
         * If it's already loaded then add to state.
         */
        if (media === 'all') {
          this.setStylesheet(stylesheet)
          return
        }

        /**
         * Otherwise listen for load.
         */
        element.addEventListener('load', () => {
          element.setAttribute('media', 'all')
          this.setStylesheet(stylesheet)
        })
      })
    },

    /**
     * Handle resize.
     */
    handleResize() {
      this.screen = {
        height: window.visualViewport.height * window.visualViewport.scale,
        width: window.innerWidth,
      }

      /**
       * Set CSS variable.
       */
      document.documentElement.style.setProperty('--viewport-height', `${this.screen.height}px`)

      for (const [breakpoint, value] of Object.entries(breakpoints)) {
        if (!window.matchMedia(`(min-width: ${value})`).matches) {
          break
        }

        this.screen.breakpoint = breakpoint
      }

      this.screen.mobile = this.settings.mobileBreakpoints.includes(this.screen.breakpoint)

      cnvs.EventBus.emit('cnvs:resize', this.screen)
      this.setScreen(this.screen)
    },

    /**
     * Handle scroll.
     * - Don't update state when overlay opens.
     * @param {Boolean} [start] - If setting on initial load.
     */
    handleScroll(start = false) {
      const html = document.documentElement
      if (this.activeOverlays.length) {
        return
      }

      const transparentClass = this.$class('transparent')
      const hasTransparentClass = html.classList.contains(transparentClass)
      const isDefaultTransparent = html.dataset.transparent
      if (window.scrollY > 100 && hasTransparentClass) {
        html.classList.remove(transparentClass)
      } else if (window.scrollY <= 100 && isDefaultTransparent && !hasTransparentClass) {
        html.classList.add(transparentClass)
      }

      /**
       * Set object on start.
       */
      if (start) {
        this.scroll = {
          direction: 'none',
          header: 'open',
          top: window.scrollY <= this.settings.start,
          x: window.scrollX,
          y: window.scrollY,
        }

        return
      }

      /**
       * If scroll is paused or if scroll difference is less than threshold
       * don't update scroll.
       */
      const difference = window.scrollY - this.scroll.y

      if (this.pausedScroll || Math.abs(difference) < this.settings.threshold) {
        return
      }

      this.scroll = {
        direction: difference > 0 ? 'down' : 'up',
        top: window.scrollY <= this.settings.start,
        x: window.scrollX,
        y: window.scrollY,
      }

      if (this.scroll.direction === 'up' && !this.scroll.top) {
        this.scroll.header = 'minimised'
      } else if (window.scrollY > this.settings.start) {
        this.scroll.header = 'collapsed'
      } else {
        this.scroll.header = 'open'
      }

      cnvs.EventBus.emit('cnvs:scroll', this.scroll)
      this.setScroll(this.scroll)
    },

    /**
     * Refocus on footer after newsletter form submission
     */
    handleNewsletterResponse() {
      const shouldFocus = ['customer_posted', 'form_type=customer'].find((key) =>
        window.location.search.includes(key),
      )

      if (!shouldFocus) {
        return
      }

      const field = document.getElementById('newsletter-field')
      if (!field) {
        return
      }

      document.documentElement.style.scrollBehavior = 'auto'
      field.focus()
    },

    /**
     * Handle account login/register success.
     */
    handleAccountSuccess() {
      const accountRegister = sessionStorage.getItem('accountRegister')
      const accountLogin = sessionStorage.getItem('accountLogin')
      const pageType = this.$variable('page.type')
      const customer = this.$variable('customer')
      const deny = ['customers/register', 'customers/activate_account']
      const loginSuccess = Boolean(accountLogin) && pageType === 'customers/account'
      const registerSuccess = Boolean(accountRegister) && !deny.includes(pageType)

      sessionStorage.removeItem('accountRegister')
      sessionStorage.removeItem('accountLogin')

      /**
       * Registration error message
       * Sometimes the error message doesn't appear on form submission
       */
      if (Boolean(accountRegister) && pageType === 'customers/register') {
        const formError = document.querySelector('[js-customer="form-error"]')
        if (formError && !formError.children[0]) {
          formError.innerHTML = `
            <p
              class="
                customers__error
                form-group__note
                text-body-m-regular
              "
            >
              ${this.$string('customers.register.registration_error_html')}
            </p>
          `
        }
      }

      if (!customer?.id || (!loginSuccess && !registerSuccess)) {
        return
      }

      loginEvent({
        id: customer.id,
        email: customer.email,
      })
    },

    /**
     * Remove gift line item automatically if not enabled on page load.
     */
    handleGwp() {
      const { status, response } = this.cart

      if (status.label === 'loading') {
        return
      }
      this.gwpWatcher()

      const { enabled, product } = this.$variable('gwp')
      if (enabled) {
        return
      }

      const giftLine = response.items.find(
        // eslint-disable-next-line no-underscore-dangle
        (item) => item.properties?._isGift && item.product_id === product,
      )

      if (!giftLine) {
        return
      }

      this.removeItem(giftLine.key)
    },

    /**
     * Clear customer data from session storage if they're logged out
     */
    clearAccountData() {
      if (this.$variable('customer.loggedIn')) {
        return
      }
      sessionStorage.removeItem('klaviyo_anniversary')
      sessionStorage.removeItem('klaviyo_birthday')
      sessionStorage.removeItem('klaviyo_experience_ids')
    },

    /**
     * Save user geolocation in Vuex store
     */
    async handleGeolocation() {
      const isGeoSet = sessionStorage.getItem('jh_geo')
      if (isGeoSet) {
        return
      }
      sessionStorage.setItem('jh_geo', 'true')

      try {
        const userGeo = await fetch('https://geo.prod20.narvar.com/postalCode').then((res) =>
          res.json(),
        )
        this.setGeolocation(userGeo)
      } catch (error) {
        console.error('[handleGeolocation]', error)
      }
    },
  },
}
</script>
